/**
 * Provides methods to interact with a user's password.
 */
export abstract class PasswordService {
  /**
   * Requests to reset the password for a user with the given email address.
   *
   * @throws UserNotFoundException
   *
   * @param email The email address of the user.
   */
  abstract resetPassword(email: string, captchaSolution?: string): Promise<void>;

  /**
   * Sets a new password for a user, using a reset token that has been obtained
   * from the email that has been sent after requesting the password reset.
   *
   * @throws UserNotFoundException
   * @throws TokenExpiredException
   * @throws InvalidTokenException
   * @throws WeakPasswordException
   *
   * @param email The users email address.
   * @param newPassword The new password of that user.
   * @param resetToken The token from the email, sent after requesting the password reset.
   */
  abstract confirmPasswordReset(
    email: string,
    newPassword: string,
    resetToken: string
  ): Promise<void>;

  /**
   * Requests to change the password for a user with the given accessToken.
   *
   * @throws AuthenticationInvalidException
   * @throws LimitExceededException
   *
   * @param oldPassword The old password of the user.
   * @param newPassword The new password of the user.
   * @param accessToken Access token obtained during registration.
   */
  abstract changePassword(
    oldPassword: string,
    newPassword: string,
    accessToken: string
  ): Promise<void>;
}
